import { cn, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { Truck } from 'lucide-react';

import type { FleetVehicleDto } from '../contractsMirror.js';

/**
 * The vehicle CARD GRID (UI-SPEC archetype A card variant — a card per vehicle, NOT a table;
 * fleet vehicles are inherently visual/sparse). Each card is `bg-card border-border rounded-md
 * p-6`, shows the vehicle photo placeholder + name + plate + status, and links to the vehicle
 * detail (`/fleet/$id`). The status is carried by TEXT (an Active/Archived label), never color
 * alone (A11Y-01). Token-only — no hardcoded hex (BRAND-06).
 */

export interface VehicleCardGridProps {
  vehicles: FleetVehicleDto[];
}

export function VehicleCardGrid({ vehicles }: VehicleCardGridProps) {
  const { t } = useTranslation();

  return (
    <ul
      // A responsive card grid (1 → 2 → 3 columns). `list-none` keeps the semantics without bullets.
      className="grid list-none grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
      aria-label={t('fleet:grid.label')}
    >
      {vehicles.map((vehicle) => {
        const archived = vehicle.archivedAt !== null;
        const subtitleParts = [
          vehicle.year ? String(vehicle.year) : null,
          vehicle.make,
          vehicle.model,
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <li key={vehicle.id}>
            <Link
              to="/fleet/$id"
              params={{ id: String(vehicle.id) }}
              className={cn(
                'flex h-full flex-col gap-4 rounded-md border border-border bg-card p-6 transition-colors',
                'hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              )}
            >
              <div className="flex items-start justify-between gap-3">
                {/* Photo placeholder — the legacy app stores no vehicle photo, so a token-styled
                    glyph tile stands in (additive redesign vocabulary, not a parity regression). */}
                <span
                  className="flex size-12 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground"
                  aria-hidden="true"
                >
                  <Truck className="size-6" />
                </span>
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 font-medium text-xs',
                    archived
                      ? 'border border-border bg-background text-muted-foreground'
                      : 'bg-primary/10 text-primary-ink',
                  )}
                >
                  {archived ? t('fleet:status.archived') : t('fleet:status.active')}
                </span>
              </div>

              <div className="min-w-0">
                <h2 className="truncate font-display font-semibold text-foreground text-lg">
                  {vehicle.displayName}
                </h2>
                {subtitleParts ? (
                  <p className="mt-0.5 truncate text-muted-foreground text-sm">{subtitleParts}</p>
                ) : null}
              </div>

              <dl className="mt-auto grid grid-cols-2 gap-2 text-sm">
                <div className="min-w-0">
                  <dt className="text-muted-foreground text-xs uppercase tracking-wide">
                    {t('fleet:card.unit')}
                  </dt>
                  <dd className="truncate text-foreground">
                    {vehicle.unitNumber ? vehicle.unitNumber : t('fleet:card.none')}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-muted-foreground text-xs uppercase tracking-wide">
                    {t('fleet:card.plate')}
                  </dt>
                  <dd className="truncate font-mono text-foreground">
                    {vehicle.licensePlate ? vehicle.licensePlate : t('fleet:card.none')}
                  </dd>
                </div>
              </dl>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
